import { Copy, Link2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { ReferableJob } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { ReferralReport } from '../../pools/components/ReferralReport';
import { useReferableJobs, useReferralLink } from '../api/publishing-api';
import styles from './ReferralsPage.module.css';

/**
 * F6.05 — "any staff". Every open, non-confidential job, and the viewer's own link for
 * each (DECISIONS-LOG S13 Q3: one link per person per job). Whoever applies through
 * it is marked as referred by them. S25 adds the referral report below, for recruiters and HR.
 */
export function ReferralsPage(): ReactNode {
  const jobs = useReferableJobs();
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Refer someone</h1>
        <p className={styles.lead}>Know someone right for one of these roles? Send them your link — their application is marked as referred by you.</p>
      </header>
      {jobs.isPending ? (
        <SkeletonLines count={6} />
      ) : jobs.error ? (
        <ServerErrorState onRetry={() => void jobs.refetch()} />
      ) : jobs.data.length === 0 ? (
        <EmptyState title="No open roles right now" body="When a job opens, it appears here with your personal link." action={null} />
      ) : (
        <ul className={styles.list}>
          {jobs.data.map((job) => (
            <JobRow key={job.jobId} job={job} />
          ))}
        </ul>
      )}
      <ReferralReport />
    </div>
  );
}

function JobRow({ job }: { readonly job: ReferableJob }): ReactNode {
  const link = useReferralLink();
  const show = useToasts((state) => state.show);
  const [url, setUrl] = useState<string | null>(job.myLink?.trackedUrl ?? null);

  async function copy(): Promise<void> {
    try {
      const target = url ?? (await link.mutateAsync(job.jobId)).trackedUrl;
      setUrl(target);
      await navigator.clipboard.writeText(target);
      show({ tone: 'success', message: 'Your link is copied', detail: 'Paste it into WhatsApp, email or wherever you share it.', key: 'referral-copy' });
    } catch (cause) {
      show({ tone: 'danger', message: "Couldn't copy your link", detail: errorMessage(cause) });
    }
  }

  return (
    <li>
      <Card>
        <div className={styles.row}>
          <div className={styles.job}>
            <span className={styles.jobTitle}>{job.title}</span>
            <span className={styles.meta}>
              {[job.ventureName, job.departmentName, job.location].filter(Boolean).join(' · ')}
            </span>
            {job.myLink && job.myLink.applicants > 0 && (
              <span className={styles.meta}>
                {formatNumber(job.myLink.applicants)} {job.myLink.applicants === 1 ? 'person has' : 'people have'} applied through your link
              </span>
            )}
          </div>
          <div className={styles.linkBox}>
            {url && (
              <span className={styles.url} title={url}>
                {url}
              </span>
            )}
            <Button variant={url ? 'quiet' : 'solid'} icon={url ? Copy : Link2} isPending={link.isPending} onClick={() => void copy()}>
              {url ? 'Copy link' : 'Get my link'}
            </Button>
          </div>
        </div>
      </Card>
    </li>
  );
}
